import React, { memo, useMemo } from 'react';

import type {
  DrawReactComponentInstruction,
  DrawReactComponentName,
  DrawReactComponentProps,
} from '@aline/common';
import { equals } from 'ramda';
import { ImageResizer } from 'src/layout-engine/components/ImageResizer';

import { CheckboxButton } from './CheckboxButton';
import { DynamicBlockPopoverAnchor } from './DynamicBlockPopoverAnchor';
import { MouseCursorOverlay } from './MouseCursorOverlay';
import { SignatureFieldPlaceholder } from './SignatureFieldPlaceholder';
import { SignatureFieldPopoverAnchor } from './SignatureFieldPopoverAnchor';
import { TableBorderResizer } from './TableBorderResizer';
import { ToggleDynamicBlockButton } from './ToggleDynamicBlockButton';
import { UpdateTableOfContentsButton } from './UpdateTableOfConentsButton';

const reactComponents: {
  [N in keyof DrawReactComponentProps]: React.FC<DrawReactComponentProps[N]>;
} = {
  CheckboxButton: memo(CheckboxButton),
  DynamicBlockPopoverAnchor: memo(DynamicBlockPopoverAnchor),
  ImageResizer: memo(ImageResizer),
  MouseCursorOverlay: memo(MouseCursorOverlay),
  SignatureFieldPlaceholder: memo(SignatureFieldPlaceholder),
  SignatureFieldPopoverAnchor: memo(SignatureFieldPopoverAnchor),
  TableBorderResizer: memo(TableBorderResizer),
  ToggleDynamicBlockButton: memo(ToggleDynamicBlockButton),
  UpdateTableOfContentsButton: memo(UpdateTableOfContentsButton),
};

export const DrawReactComponent = React.memo(function DrawReactComponent<
  N extends DrawReactComponentName,
>({ clipRect, componentName, props, x, y, zIndex }: Omit<DrawReactComponentInstruction<N>, 'key'>) {
  const Component = Object.hasOwn(reactComponents, componentName)
    ? (reactComponents[componentName] as React.FC<DrawReactComponentProps[N]>)
    : undefined;
  if (!Component) throw new Error(`Missing component for ${componentName}`);

  const clipPath: string | undefined = useMemo(() => {
    if (!clipRect) return undefined;
    const relativeStartX = clipRect.x - x;
    const relativeStartY = clipRect.y - y;
    const relativeEndX = relativeStartX + clipRect.width;
    const relativeEndY = relativeStartY + clipRect.height;
    return `rect(${relativeStartY}px ${relativeEndX}px ${relativeEndY}px ${relativeStartX}px)`;
  }, [clipRect, x, y]);

  return (
    <div style={{ clipPath, left: x, position: 'absolute', top: y, zIndex }}>
      <Component {...props} />
    </div>
  );
}, equals);
